import type { BakedBlockInstance, Placement } from '@tsagko/label-contracts'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { LaidOutZone } from './layout'
import { SplitButtons } from './SplitButtons'
import type { SplitDirection } from './zone-ops'

interface ZoneRendererProps {
  zone: LaidOutZone
  pxPerMm: number
  onSelect?: (zone: LaidOutZone) => void
  onSplit?: (zone: LaidOutZone, direction: SplitDirection) => void
  onDrop?: (payload: string, zone: LaidOutZone) => void
  isSelected?: boolean
  isPropped?: boolean
  pickMode?: boolean
}

/**
 * Returns the primitive's `placement` if defined, mirroring the schema
 * default ({ left, top }) from Task 1 of the block-variables slice. Returns
 * `undefined` for baked-block-instances (their placement is host-level).
 */
function readPrimitivePlacement(component: LaidOutZone['component']): Placement | undefined {
  if (component.type === 'baked-block-instance') return undefined
  return component.placement
}

export function ZoneRenderer({
  zone,
  pxPerMm,
  onSelect,
  onSplit,
  onDrop,
  isSelected,
  isPropped,
  pickMode,
}: ZoneRendererProps) {
  const { t } = useTranslation()
  const [isDragOver, setIsDragOver] = useState(false)

  const dragHandlers = onDrop
    ? {
        onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'copy'
          setIsDragOver(true)
        },
        onDragLeave: () => setIsDragOver(false),
        onDrop: (e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault()
          setIsDragOver(false)
          // Prefer the custom MIME type; fall back to text/plain for jsdom tests.
          const payload =
            e.dataTransfer.getData('application/x-label-block') ||
            e.dataTransfer.getData('text/plain')
          if (payload) onDrop(payload, zone)
        },
      }
    : {}
  const style = {
    position: 'absolute' as const,
    left: zone.x * pxPerMm,
    top: zone.y * pxPerMm,
    width: zone.w * pxPerMm,
    height: zone.h * pxPerMm,
  }
  const hasBlackBackground = zone.style?.backgroundColor === 'black'

  // Presentation-only branch: zones nested inside a baked-block-instance's
  // expansion carry `bakedDepth > 0`. They paint zone-level backgrounds so
  // the block looks the way it did in the configurator, but they don't
  // surface as interactive targets — no dashed border, no click handlers,
  // no split buttons, and pointer events fall through to the host zone.
  if (zone.bakedDepth > 0) {
    return (
      <div
        data-testid={`zone-${zone.id}`}
        data-zone-id={zone.id}
        aria-hidden="true"
        className="pointer-events-none absolute"
        style={style}
      >
        {hasBlackBackground && (
          <div
            data-testid="zone-background"
            aria-hidden="true"
            className="absolute inset-0"
            style={{ backgroundColor: 'black' }}
          />
        )}
      </div>
    )
  }

  const component = zone.component
  const isEmpty = (component as unknown) === undefined
  if (isEmpty) {
    if (pickMode) {
      return (
        <div
          data-testid={`zone-${zone.id}`}
          data-zone-id={zone.id}
          role="button"
          tabIndex={0}
          onClick={() => onSelect?.(zone)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') onSelect?.(zone)
          }}
          {...dragHandlers}
          className={[
            'group absolute cursor-pointer border-2 border-dashed border-fuchsia-500 bg-fuchsia-50/20 hover:bg-fuchsia-100/50 dark:bg-fuchsia-950/20 transition',
            isDragOver ? 'bg-fuchsia-100/50' : '',
          ].join(' ')}
          style={style}
        />
      )
    }
    return (
      <div
        data-testid={`zone-${zone.id}`}
        data-zone-id={zone.id}
        role="button"
        tabIndex={0}
        onClick={() => onSelect?.(zone)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') onSelect?.(zone)
        }}
        {...dragHandlers}
        className={[
          'group absolute cursor-pointer border border-dashed border-stone-300 transition hover:border-stone-500 dark:border-stone-400',
          isDragOver ? 'border-fuchsia-500 bg-fuchsia-50/20' : '',
        ].join(' ')}
        style={style}
      >
        <div className="flex h-full w-full items-center justify-center text-[10px] text-stone-400">
          {/* Only advertise dropping where a drop handler is actually wired
              (the block configurator renders without one — issue #6). */}
          {onDrop ? t('canvas.zone.dropBlockHere') : t('canvas.zone.emptyRegion')}
        </div>
        {onSplit && <SplitButtons onSplit={dir => onSplit(zone, dir)} />}
      </div>
    )
  }
  const isBakedInstance = component.type === 'baked-block-instance'
  const placement = readPrimitivePlacement(component)

  // In pick mode, occupied zones (those containing a baked-block-instance) are
  // visually distinct from empty ones: amber dashed = "replace", fuchsia = "drop
  // into empty slot". This gives the operator a clear signal before committing.
  // During a drag-over we apply the same colour semantics regardless of pick mode.
  const borderClass = isDragOver
    ? isBakedInstance
      ? 'border-2 border-dashed border-amber-500 bg-amber-100/50 dark:bg-amber-950/20'
      : 'border-2 border-dashed border-fuchsia-500 bg-fuchsia-100/50 dark:bg-fuchsia-950/20'
    : pickMode
      ? isBakedInstance
        ? 'border-2 border-dashed border-amber-500 bg-amber-50/20 hover:bg-amber-100/50 dark:bg-amber-950/20'
        : 'border-2 border-dashed border-fuchsia-500 bg-fuchsia-50/20 hover:bg-fuchsia-100/50 dark:bg-fuchsia-950/20'
      : isSelected
        ? 'border border-sky-500 bg-sky-50/60'
        : isBakedInstance
          ? 'border-2 border-indigo-500 bg-indigo-50/30 dark:bg-indigo-950/20'
          : isPropped
            ? 'border-2 border-fuchsia-500 bg-fuchsia-50/40 dark:bg-fuchsia-950/20'
            : 'border border-dashed border-stone-300 hover:border-stone-500 dark:border-stone-400'

  return (
    <div
      data-testid={`zone-${zone.id}`}
      data-zone-id={zone.id}
      data-block-instance-id={
        isBakedInstance ? (component as BakedBlockInstance).id : undefined
      }
      data-block-id={
        isBakedInstance ? (component as BakedBlockInstance).blockId : undefined
      }
      data-pick-occupied={pickMode && isBakedInstance ? 'true' : undefined}
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(zone)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onSelect?.(zone)
      }}
      {...dragHandlers}
      className={`group absolute cursor-pointer transition ${borderClass}`}
      style={style}
      data-placement-h={placement?.hAlign}
      data-placement-v={placement?.vAlign}
    >
      {hasBlackBackground && (
        <div
          data-testid="zone-background"
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ backgroundColor: 'black' }}
        />
      )}
      {onSplit && !pickMode && (
        <SplitButtons onSplit={dir => onSplit(zone, dir)} />
      )}
    </div>
  )
}
